import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminAuthService } from '../admin-auth.service';
import { ApplicantService } from 'app/laas/applicant.service';
import { AdvancedFilterService } from 'app/core/advanced-filter/advanced-filter.service';

@Component({
  selector: 'wl-stale-users-report',
  templateUrl: './stale-users-report.component.html',
  styles: [`
    :host ::ng-deep .button:hover {
      opacity: 0.85;
      box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    }
    :host ::ng-deep .button:active {
      opacity: 0.7;
      transform: scale(0.97);
    }
    .export-excel-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: #db2016;
      cursor: pointer;
      transition: background-color 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;
    }
    .export-excel-btn:hover:not([disabled]) {
      background-color: rgba(219, 32, 22, 0.08);
      box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    }
    .export-excel-btn:active:not([disabled]) {
      transform: scale(0.96);
      background-color: rgba(219, 32, 22, 0.15);
    }
    .export-excel-btn-disabled {
      cursor: not-allowed !important;
      opacity: 0.5;
      color: #999 !important;
    }
  `]
})
export class StaleUsersReportComponent implements OnInit {
  reportData: any[] = [];
  loading = false;
  errorMsg = '';
  submitted = false;

  constructor(
    private adminAuth: AdminAuthService,
    private applicant: ApplicantService,
    private advancedFilterService: AdvancedFilterService,
    private snackbar: MatSnackBar
  ) {}

  ngOnInit() {}

  applyFilter() {
    this.submitted = true;
    this.loading = true;
    this.errorMsg = '';
    this.reportData = [];

    const formData = this.advancedFilterService.getFilterData();

    this.adminAuth.getStaleUsersReportFiltered(formData).subscribe(
      data => {
        let result = [];

        if (Array.isArray(data)) {
          result = data;
        } else if (data && data.result && Array.isArray(data.result)) {
          result = data.result;
        }

        this.reportData = result.map(item => {
          const lastLogin = item.last_login_timestamp
            ? new Date(item.last_login_timestamp)
            : null;

          const disabledDate = item.disabled_date
            ? new Date(item.disabled_date)
            : null;

          let daysSinceLastLogin = '—';

          if (lastLogin) {
            const now = new Date();
            const diffTime = now.getTime() - lastLogin.getTime();
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            daysSinceLastLogin = String(diffDays);
          }

          return {
            ...item,
            last_login_timestamp: lastLogin,
            disabled_date: disabledDate,
            daysSinceLastLogin
          };
        });

        this.loading = false;
      },
      err => {
        this.errorMsg = (err && err.message) ? err.message : 'Failed to load report.';
        this.loading = false;
      }
    );
  }

  unlockStaleUsers(userId: string) {
    this.loading = true;
    this.applicant.unlockStaleUsers(sessionStorage.getItem("getuserId"), userId).subscribe(
      (resp) => {
        console.log("unlockStaleUsers response:", resp);
        if (resp && resp.success) {
          this.snackbar.open('User unlocked successfully', 'OK', { duration: 3000 });
          this.applyFilter();
        } else {
          this.loading = false;
          this.snackbar.open(resp && resp.message || 'Failed to unlock user', 'OK', { duration: 4000 });
        }
      },
      (err) => {
        this.loading = false;
        this.snackbar.open(err && err.message || 'Failed to unlock user', 'OK', { duration: 4000 });
      }
    );
  }

  
  exportExcel() {
    if (!this.reportData.length) return;

    const fmt = (d: Date | null): string => {
      if (!d) return '';
      const p = (n: number) => { var s = String(n); return s.length < 2 ? '0' + s : s; };
      const hr12 = d.getHours() % 12 || 12;
      return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(hr12)}:${p(d.getMinutes())} ${d.getHours() >= 12 ? 'PM' : 'AM'}`;
    };

    const s = (v: any) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const hCell = (v: string) => `<Cell ss:StyleID="H"><Data ss:Type="String">${s(v)}</Data></Cell>`;
    const dCell = (v: any)    => `<Cell ss:StyleID="D"><Data ss:Type="String">${s(v)}</Data></Cell>`;
    const nCell = (v: any)    => `<Cell ss:StyleID="N"><Data ss:Type="Number">${s(v)}</Data></Cell>`;

    const headerRow = `<Row ss:Height="22">
      ${['#','Staff ID','Full Name','Branch','Zone','Last Login Date','Account Status','Days Since Last Login','Disabled Date'].map(hCell).join('')}
    </Row>`;

    const dataRows = this.reportData.map((r, i) => `<Row ss:Height="18">
      ${nCell(i + 1)}
      ${dCell(r.staffId || '')}
      ${dCell(r.staffName || '')}
      ${dCell(r.branch_name || '')}
      ${dCell(r.zone_name || '')}
      ${dCell(fmt(r.last_login_timestamp))}
      ${dCell(r.login_status || '')}
      ${dCell(r.daysSinceLastLogin !== '—' ? r.daysSinceLastLogin + ' days' : '')}
      ${dCell(fmt(r.disabled_date))}
    </Row>`).join('');

    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:x="urn:schemas-microsoft-com:office:excel"
  xmlns:o="urn:schemas-microsoft-com:office:office">
  <Styles>
    <Style ss:ID="H">
      <Font ss:Bold="1" ss:Color="#FFFFFF" ss:Size="11" ss:FontName="Calibri"/>
      <Interior ss:Color="#1E3A5F" ss:Pattern="Solid"/>
      <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="0"/>
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#FFFFFF"/>
      </Borders>
    </Style>
    <Style ss:ID="D">
      <Font ss:Size="11" ss:FontName="Calibri"/>
      <Alignment ss:Vertical="Center"/>
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D0D0D0"/>
      </Borders>
    </Style>
    <Style ss:ID="N">
      <Font ss:Size="11" ss:FontName="Calibri"/>
      <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D0D0D0"/>
      </Borders>
    </Style>
  </Styles>
  <Worksheet ss:Name="Stale Users Report">
    <Table>
      <Column ss:Width="35"/>
      <Column ss:Width="70"/>
      <Column ss:Width="120"/>
      <Column ss:Width="110"/>
      <Column ss:Width="100"/>
      <Column ss:Width="145"/>
      <Column ss:Width="100"/>
      <Column ss:Width="130"/>
      <Column ss:Width="145"/>
      ${headerRow}
      ${dataRows}
    </Table>
    <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
      <FreezePanes/>
      <FrozenNoSplit/>
      <SplitHorizontal>1</SplitHorizontal>
      <TopRowBottomPane>1</TopRowBottomPane>
      <ActivePane>2</ActivePane>
    </WorksheetOptions>
  </Worksheet>
</Workbook>`;

    const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stale-users-report.xls';
    a.click();
    window.URL.revokeObjectURL(url);
  }

 
}
